/* global __firebase_config, __initial_auth_token, __app_id */
import React, { useState, useEffect, createContext, useContext } from 'react';
import { initializeApp } from 'firebase/app';
import { getAuth, signInAnonymously, signInWithCustomToken, onAuthStateChanged } from 'firebase/auth';
import { getFirestore, doc, getDoc, setDoc, onSnapshot, collection, addDoc, query, where, deleteDoc } from 'firebase/firestore';

// Lucide React icons for a modern look
import { Users, FileText, Upload, UserCheck, BarChart3, Search, Plus, Trash2, Eye, Sparkles, Target, Settings, Shield } from 'lucide-react';


// Context for Firebase and User data
const AppContext = createContext(null);

const AppProvider = ({ children }) => {
  const [db, setDb] = useState(null);
  const [auth, setAuth] = useState(null);
  const [userId, setUserId] = useState(null);
  const [isAuthReady, setIsAuthReady] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    try {
      // Firebase configuration and initialization
      // IMPORTANT: Replace the placeholder values below with YOUR ACTUAL Firebase project config.
      // You can find these in your Firebase Console -> Project settings -> General.
      const firebaseConfig = typeof __firebase_config !== 'undefined' ? JSON.parse(__firebase_config) : {
       apiKey: "AIzaSyA-uV70MGlp5xl8a5AEC5yRSTM5xsVa9N4", // Replace with your actual Firebase API Key
        authDomain: "https://visual-aid-2b9f3.web.app", // Replace with your actual Firebase Auth Domain
        projectId: "visual-aid-2b9f3", // Replace with your actual Firebase Project ID
        storageBucket: "your-project-id.appspot.com", // Replace with your actual Firebase Storage Bucket
        messagingSenderId: "your-messaging-sender-id", // Replace with your actual Firebase Messaging Sender ID
        appId: "your-app-id" // Replace with your actual Firebase App ID
      };
      
      const app = initializeApp(firebaseConfig);
      const firestoreDb = getFirestore(app);
      const firebaseAuth = getAuth(app);

      setDb(firestoreDb);
      setAuth(firebaseAuth);

      // Firebase Authentication
      const unsubscribe = onAuthStateChanged(firebaseAuth, async (user) => {
        if (user) {
          setUserId(user.uid);
          setIsAuthReady(true);
        } else {
          // Sign in anonymously if no user is found
          try {
            // Initial auth token is provided by Canvas, use anonymous sign-in if not available.
            const initialAuthToken = typeof __initial_auth_token !== 'undefined' ? __initial_auth_token : null;
            if (initialAuthToken) {
              await signInWithCustomToken(firebaseAuth, initialAuthToken);
            } else {
              await signInAnonymously(firebaseAuth);
            }
            // Ensure userId is set after successful sign-in
            setUserId(firebaseAuth.currentUser?.uid || crypto.randomUUID());
            setIsAuthReady(true);
          } catch (e) {
            console.error("Error signing in anonymously/with custom token:", e);
            setError("Failed to authenticate. Please check Firebase configuration and ensure anonymous authentication is enabled in your Firebase project.");
            setIsAuthReady(true); // Still set ready to avoid infinite loading
          }
        }
        setLoading(false);
      });

      return () => unsubscribe(); // Cleanup auth listener
    } catch (e) {
      console.error("Firebase initialization error:", e);
      setError("Failed to initialize the application. Please ensure your Firebase config is correct.");
      setLoading(false);
    }
  }, []); // Empty dependency array means this runs once on mount

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-[#F4F4F4] to-[#E0E0E0]">
        <div className="flex flex-col items-center p-8 bg-white rounded-2xl shadow-xl border border-gray-200">
          <div className="w-16 h-16 border-4 border-t-4 border-[#2A9D8F] border-solid rounded-full animate-spin mb-4"></div>
          <div className="text-xl font-semibold text-[#333333]">Loading application...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-red-100">
        <div className="bg-red-50 border border-red-200 text-red-800 p-6 rounded-xl shadow-lg text-center max-w-md">
          <p className="text-lg font-semibold mb-2">Application Error</p>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <AppContext.Provider value={{ db, auth, userId, isAuthReady }}>
      {children}
    </AppContext.Provider>
  );
};

// Reusable Message Component
const Message = ({ message, type }) => {
  if (!message) return null;
  
  const getMessageConfig = () => {
    switch (type) {
      case 'success':
        return {
          bg: 'bg-[#2A9D8F]/10', // Teal Green
          border: 'border-[#2A9D8F]/30',
          text: 'text-[#2A9D8F]',
          icon: <Sparkles className="w-5 h-5 text-[#2A9D8F]" />
        };
      case 'error':
        return {
          bg: 'bg-red-50',
          border: 'border-red-200',
          text: 'text-red-800',
          icon: <Shield className="w-5 h-5 text-red-600" />
        };
      default: // Info messages
        return {
          bg: 'bg-[#E9C46A]/10', // Soft Amber
          border: 'border-[#E9C46A]/30',
          text: 'text-[#E9C46A]',
          icon: <Settings className="w-5 h-5 text-[#E9C46A]" />
        };
    }
  };

  const config = getMessageConfig();

  return (
    <div className={`p-4 mb-6 rounded-xl border ${config.bg} ${config.text} ${config.border} transition-all duration-300 ease-in-out`}>
      <div className="flex items-start space-x-3">
        <div className="flex-shrink-0 mt-0.5">
          {config.icon}
        </div>
        <div className="flex-1 text-sm font-medium">{message}</div>
      </div>
    </div>
  );
};

const Dashboard = () => {
  const { db, userId, isAuthReady } = useContext(AppContext);
  const [users, setUsers] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [newName, setNewName] = useState('');
  const [newDocName, setNewDocName] = useState('');
  const [newDocType, setNewDocType] = useState('pdf');
  const [newDocContent, setNewDocContent] = useState('');
  const [selectedUserForAssignment, setSelectedUserForAssignment] = useState('');
  const [selectedDocForAssignment, setSelectedDocForAssignment] = useState('');
  const [assignedPagesInput, setAssignedPagesInput] = useState('');
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState(''); // 'success', 'error', 'info'

  // State for LLM features
  const [isSummarizingDoc, setIsSummarizingDoc] = useState(false);
  const [generatedDocSummary, setGeneratedDocSummary] = useState('');

  const appId = typeof __app_id !== 'undefined' ? __app_id : 'default-app-id';
  // Use private collection paths for user-specific data
  const privateUsersCollectionPath = `artifacts/${appId}/users/${userId}/app_users`;
  const privateDocsCollectionPath = `artifacts/${appId}/users/${userId}/app_documents`;

  useEffect(() => {
    if (!db || !isAuthReady || !userId) return;

    // Listen for users in the current user's private collection
    const usersQuery = collection(db, privateUsersCollectionPath);
    const unsubscribeUsers = onSnapshot(usersQuery, (snapshot) => {
      const usersData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setUsers(usersData);
    }, (error) => console.error("Error fetching users:", error));

    // Listen for documents in the current user's private collection
    const docsQuery = collection(db, privateDocsCollectionPath);
    const unsubscribeDocs = onSnapshot(docsQuery, (snapshot) => {
      const docsData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setDocuments(docsData);
    }, (error) => console.error("Error fetching documents:", error));

    return () => {
      // Unsubscribe from listeners when component unmounts
      unsubscribeUsers();
      unsubscribeDocs();
    };
  }, [db, userId, isAuthReady, privateUsersCollectionPath, privateDocsCollectionPath]);

  // Helper function to display temporary messages
  const showMessage = (msg, type) => {
    setMessage(msg);
    setMessageType(type);
    setTimeout(() => setMessage(''), 3000); // Clear message after 3 seconds
  };

  // Handles adding a new user to Firestore
  const handleAddUser = async () => {
    if (newName.trim() === '') {
      showMessage("User name cannot be empty.", "error");
      return;
    }
    try {
      await addDoc(collection(db, privateUsersCollectionPath), { name: newName.trim() });
      setNewName(''); // Clear input after adding
      showMessage("User added successfully!", "success");
    } catch (e) {
      console.error("Error adding user: ", e);
      showMessage("Failed to add user.", "error");
    }
  };

  // Handles deleting a user from Firestore
  const handleDeleteUser = async (id) => {
    try {
      await deleteDoc(doc(db, privateUsersCollectionPath, id));
      showMessage("User deleted successfully!", "success");
    } catch (e) {
      console.error("Error deleting user: ", e);
      showMessage("Failed to delete user.", "error");
    }
  };

  // Handles simulating document upload and saving metadata to Firestore
  const handleUploadDocument = async () => {
    if (newDocName.trim() === '' || newDocContent.trim() === '') {
      showMessage("Document name and content cannot be empty.", "error");
      return;
    }
    try {
      const docRef = await addDoc(collection(db, privateDocsCollectionPath), {
        name: newDocName.trim(),
        type: newDocType,
        content: newDocContent.split('\n'), // Each line is a simulated 'page'
        assignedTo: {}, // Object to store user-page assignments
      });
      console.log("Document uploaded with ID:", docRef.id);
      setNewDocName('');
      setNewDocType('pdf');
      setNewDocContent('');
      showMessage("Document uploaded successfully!", "success");
      setGeneratedDocSummary(''); // Clear previous summary
    } catch (e) {
      console.error("Error uploading document: ", e);
      showMessage("Failed to upload document.", "error");
    }
  };

  // Handles assigning pages/sections of a document to a specific user
  const handleAssignPages = async () => {
    if (!selectedUserForAssignment || !selectedDocForAssignment || assignedPagesInput.trim() === '') {
      showMessage("Please select a user, document, and enter page assignments.", "error");
      return;
    }

    try {
      const docRef = doc(db, privateDocsCollectionPath, selectedDocForAssignment);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const currentAssignedTo = docSnap.data().assignedTo || {};
        // Update the assigned pages for the selected user
        const updatedAssignedTo = {
          ...currentAssignedTo,
          [selectedUserForAssignment]: assignedPagesInput.trim(),
        };

        // Use setDoc with merge: true to update only the 'assignedTo' field
        await setDoc(docRef, { assignedTo: updatedAssignedTo }, { merge: true });
        showMessage("Pages assigned successfully!", "success");
        // Clear selection and input after assignment
        setAssignedPagesInput('');
        setSelectedUserForAssignment('');
        setSelectedDocForAssignment('');
      } else {
        showMessage("Document not found.", "error");
      }
    } catch (e) {
      console.error("Error assigning pages: ", e);
      showMessage("Failed to assign pages.", "error");
    }
  };

  // Gemini API Integration: Summarize Document Content
  const handleSummarizeDocument = async (documentContentArray) => {
    if (!documentContentArray || documentContentArray.length === 0) {
      showMessage("No document content to summarize.", "info");
      return;
    }

    setIsSummarizingDoc(true);
    setGeneratedDocSummary(''); // Clear previous summary
    showMessage("Generating summary...", "info");

    const prompt = `Summarize the following document content concisely. Each line represents a page or section:\n\n${documentContentArray.join('\n')}`;

    try {
      let chatHistory = [];
      chatHistory.push({ role: "user", parts: [{ text: prompt }] });
      const payload = { contents: chatHistory };
      const apiKey = ""; // Canvas will provide this at runtime
      const apiUrl = `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.0-flash:generateContent?key=${apiKey}`;

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      const result = await response.json();

      if (result.candidates && result.candidates.length > 0 &&
          result.candidates[0].content && result.candidates[0].content.parts &&
          result.candidates[0].content.parts.length > 0) {
        const text = result.candidates[0].content.parts[0].text;
        setGeneratedDocSummary(text);
        showMessage("Document summary generated successfully!", "success");
      } else {
        showMessage("Failed to generate summary. No content found in response.", "error");
        console.error("Gemini API response structure unexpected:", result);
      }
    } catch (e) {
      console.error("Error calling Gemini API for document summary:", e);
      showMessage("Error generating summary. Please try again later.", "error");
    } finally {
      setIsSummarizingDoc(false);
    }
  };

  return (
    <div className="p-4 sm:p-8 bg-gradient-to-br from-[#F4F4F4] to-[#E0E0E0] min-h-screen font-sans text-[#333333]">
      <h1 className="text-3xl sm:text-4xl font-extrabold text-[#264653] mb-8 text-center">
        Admin Dashboard
      </h1>

      <Message message={message} type={messageType} />

      {/* Current User ID Display */}
      <div className="mb-8 p-6 bg-[#E9C46A]/10 border border-[#E9C46A]/20 rounded-2xl shadow-sm animate-fade-in-up">
          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0">
              <Settings className="w-6 h-6 text-[#E9C46A] mt-1" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-[#264653] mb-2">Workspace Configuration</h3>
              <p className="text-sm text-[#666666] mb-2">
                <span className="font-medium">Your User ID:</span> 
                <span className="font-mono text-xs ml-2 px-3 py-1 bg-[#E9C46A]/20 text-[#264653] rounded-full select-all break-all">{userId}</span>
              </p>
              <p className="text-xs text-[#666666]">
                Share this ID with team members to collaborate on the same documents and user management.
              </p>
            </div>
          </div>
        </div>

      {/* Add New User */}
      <section className="mb-10 bg-white p-6 sm:p-8 rounded-2xl shadow-xl border border-gray-200 transition-all duration-300 hover:shadow-2xl animate-fade-in-up">
        <h2 className="text-2xl font-bold text-[#264653] mb-6 flex items-center">
          <Users className="w-7 h-7 mr-3 text-[#2A9D8F]" />
          Manage Users
        </h2>
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <input
            type="text"
            placeholder="Enter new user name"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            className="flex-grow p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2A9D8F] focus:border-transparent outline-none transition duration-200 text-base text-[#333333]"
          />
          <button
            onClick={handleAddUser}
            className="px-6 py-3 bg-gradient-to-r from-[#2A9D8F] to-[#264653] text-white font-semibold rounded-lg shadow-md hover:from-[#264653] hover:to-[#2A9D8F] focus:outline-none focus:ring-2 focus:ring-[#2A9D8F] focus:ring-offset-2 transition duration-200 transform hover:scale-105 active:scale-95 text-base flex items-center justify-center"
          >
            <Plus className="w-5 h-5 mr-2" />Add User
          </button>
        </div>
        <div>
          <h3 className="text-xl font-semibold text-[#264653] mb-4 border-b pb-2 border-gray-200">Team Members ({users.length}):</h3>
          {users.length === 0 ? (
            <p className="text-[#666666] italic p-2">No users added yet. Add some users above!</p>
          ) : (
            <ul className="space-y-3">
              {users.map((user) => (
                <li key={user.id} className="flex flex-col sm:flex-row justify-between items-center bg-[#F4F4F4] p-3 sm:p-4 rounded-lg border border-gray-200 shadow-sm animate-fade-in text-base">
                  <span className="font-medium text-[#333333] mb-2 sm:mb-0">{user.name}</span>
                  <button
                    onClick={() => handleDeleteUser(user.id)}
                    className="px-4 py-2 bg-red-500 text-white text-sm font-semibold rounded-md hover:bg-red-600 transition duration-200 transform hover:scale-105 active:scale-95 flex items-center"
                  >
                    <Trash2 className="w-4 h-4 mr-1" />Delete
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>

      {/* Upload New Document */}
      <section className="mb-10 bg-white p-6 sm:p-8 rounded-2xl shadow-xl border border-gray-200 transition-all duration-300 hover:shadow-2xl animate-fade-in-up">
        <h2 className="text-2xl font-bold text-[#264653] mb-6 flex items-center">
          <Upload className="w-7 h-7 mr-3 text-[#2A9D8F]" />
          Upload Document (Simulated Content Only)
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <input
            type="text"
            placeholder="Document Name (e.g., Q2 Sales Report)"
            value={newDocName}
            onChange={(e) => setNewDocName(e.target.value)}
            className="p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2A9D8F] focus:border-transparent outline-none transition duration-200 text-base text-[#333333]"
          />
          <select
            value={newDocType}
            onChange={(e) => setNewDocType(e.target.value)}
            className="p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2A9D8F] focus:border-transparent outline-none transition duration-200 bg-white text-base text-[#333333]"
          >
            <option value="pdf">📄 PDF</option>
            <option value="ppt">📊 PPT</option>
            <option value="word">📝 Word</option>
            <option value="excel">📈 Excel</option>
            <option value="image">🖼️ Image</option>
            <option value="text">📋 Text</option>
          </select>
        </div>
        <textarea
          placeholder="Enter document content here. Each new line will be treated as a new 'page' or section.

Example content:
Page 1: Executive Summary - Q2 Performance Overview
Page 2: Regional Sales Analysis - North America Results
Page 3: Product Performance Metrics and KPIs
Page 4: Market Trends and Competitive Analysis
Page 5: Strategic Recommendations for Q3"
          value={newDocContent}
          onChange={(e) => setNewDocContent(e.target.value)}
          rows="8"
          className="w-full p-3 border border-gray-300 rounded-lg mb-6 focus:ring-2 focus:ring-[#2A9D8F] focus:border-transparent outline-none transition duration-200 text-base text-[#333333] resize-y"
        ></textarea>
        <button
          onClick={handleUploadDocument}
          className="w-full px-6 py-3 bg-gradient-to-r from-[#2A9D8F] to-[#264653] text-white font-semibold rounded-lg shadow-md hover:from-[#264653] hover:to-[#2A9D8F] focus:outline-none focus:ring-2 focus:ring-[#2A9D8F] focus:ring-offset-2 transition duration-200 transform hover:scale-105 active:scale-95 text-base flex items-center justify-center"
        >
          <Upload className="w-5 h-5 mr-2" />Upload Document
        </button>
      </section>

      {/* Assign Pages to Users */}
      <section className="mb-10 bg-white p-6 sm:p-8 rounded-2xl shadow-xl border border-gray-200 transition-all duration-300 hover:shadow-2xl animate-fade-in-up">
        <h2 className="text-2xl font-bold text-[#264653] mb-6 flex items-center">
          <UserCheck className="w-7 h-7 mr-3 text-[#2A9D8F]" />
          Assign Document Content to Users
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <select
            value={selectedUserForAssignment}
            onChange={(e) => setSelectedUserForAssignment(e.target.value)}
            className="p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2A9D8F] focus:border-transparent outline-none transition duration-200 bg-white text-base text-[#333333]"
          >
            <option value="">Select User</option>
            {users.map((user) => (
              <option key={user.id} value={user.id}>{user.name}</option>
            ))}
          </select>
          <select
            value={selectedDocForAssignment}
            onChange={(e) => setSelectedDocForAssignment(e.target.value)}
            className="p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2A9D8F] focus:border-transparent outline-none transition duration-200 bg-white text-base text-[#333333]"
          >
            <option value="">Select Document</option>
            {documents.map((doc) => (
              <option key={doc.id} value={doc.id}>{doc.name} ({doc.type.toUpperCase()})</option>
            ))}
          </select>
        </div>
        <input
          type="text"
          placeholder="Enter page numbers/ranges (e.g., 1-5, 8, 10-12)"
          value={assignedPagesInput}
          onChange={(e) => setAssignedPagesInput(e.target.value)}
          className="w-full p-3 border border-gray-300 rounded-lg mb-6 focus:ring-2 focus:ring-[#2A9D8F] focus:border-transparent outline-none transition duration-200 text-base text-[#333333]"
        />
        <button
          onClick={handleAssignPages}
          className="w-full px-6 py-3 bg-gradient-to-r from-[#2A9D8F] to-[#264653] text-white font-semibold rounded-lg shadow-md hover:from-[#264653] hover:to-[#2A9D8F] focus:outline-none focus:ring-2 focus:ring-[#2A9D8F] focus:ring-offset-2 transition duration-200 transform hover:scale-105 active:scale-95 text-base flex items-center justify-center"
        >
          <Target className="w-5 h-5 mr-2" />Assign Pages
        </button>
      </section>

      {/* Display Existing Documents and their assignments */}
      <section className="mb-10 bg-white p-6 sm:p-8 rounded-2xl shadow-xl border border-gray-200 transition-all duration-300 hover:shadow-2xl animate-fade-in-up">
        <h2 className="text-2xl font-bold text-[#264653] mb-6 flex items-center">
          <BarChart3 className="w-7 h-7 mr-3 text-[#2A9D8F]" />
          Document Assignments Summary
        </h2>
        {documents.length === 0 ? (
          <p className="text-[#666666] italic p-2">No documents uploaded yet. Start by uploading one!</p>
        ) : (
          <ul className="space-y-6">
            {documents.map((docItem) => (
              <li key={docItem.id} className="bg-[#F4F4F4] p-4 rounded-lg border border-gray-200 shadow-sm animate-fade-in-up">
                <h4 className="text-xl font-semibold text-[#333333] mb-2 border-b pb-2 border-gray-200">
                  {docItem.name} <span className="text-sm font-normal text-[#666666]">({docItem.type.toUpperCase()})</span>
                </h4>
                <p className="text-sm text-[#666666] mb-3">Total 'pages': {docItem.content ? docItem.content.length : 0}</p>
                {Object.keys(docItem.assignedTo || {}).length === 0 ? (
                  <p className="text-[#666666] italic text-sm">No assignments for this document yet.</p>
                ) : (
                  <div>
                    <h5 className="text-md font-medium text-[#333333] mt-4 mb-2">Assigned To:</h5>
                    <ul className="list-disc list-inside text-sm text-[#666666] space-y-1">
                      {Object.entries(docItem.assignedTo).map(([userIdAssigned, pagesAssigned]) => {
                        const assignedUser = users.find(u => u.id === userIdAssigned);
                        return (
                          <li key={userIdAssigned} className="mb-1">
                            <span className="font-semibold text-[#333333]">{assignedUser ? assignedUser.name : 'Unknown User'}:</span> <span className="text-[#2A9D8F]">{pagesAssigned}</span>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}
                {/* Gemini API Integration: Summarize Document Content */}
                {docItem.content && docItem.content.length > 0 && (
                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <button
                      onClick={() => handleSummarizeDocument(docItem.content)}
                      className="w-full px-4 py-2 bg-gradient-to-r from-[#E9C46A] to-[#F4A261] text-[#264653] font-semibold rounded-lg shadow-md hover:from-[#F4A261] hover:to-[#E9C46A] focus:outline-none focus:ring-2 focus:ring-[#E9C46A] focus:ring-offset-2 transition duration-200 transform hover:scale-105 active:scale-95 text-sm flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                      disabled={isSummarizingDoc}
                    >
                      <Sparkles className="w-5 h-5 mr-2" />
                      {isSummarizingDoc ? (
                        <>
                          <div className="w-4 h-4 border-2 border-t-2 border-[#264653] border-solid rounded-full animate-spin mr-2"></div>
                          Summarizing...
                        </>
                      ) : (
                        'Generate AI Summary of Content'
                      )}
                    </button>
                    {generatedDocSummary && !isSummarizingDoc && (
                      <div className="mt-3 p-3 bg-[#264653]/10 border border-[#264653]/20 rounded-md text-sm text-[#264653] animate-fade-in">
                        <p className="font-semibold mb-1">Summary:</p>
                        <p>{generatedDocSummary}</p>
                      </div>
                    )}
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
};

const UserView = () => {
  const { db, userId, isAuthReady } = useContext(AppContext);
  const [users, setUsers] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [selectedUser, setSelectedUser] = useState('');
  const [displayedContent, setDisplayedContent] = useState([]);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');

  // State for LLM features
  const [isSummarizingUserContent, setIsSummarizingUserContent] = useState(false);
  const [generatedUserContentSummary, setGeneratedUserContentSummary] = useState('');

  const appId = typeof __app_id !== 'undefined' ? __app_id : 'default-app-id';
  const privateUsersCollectionPath = `artifacts/${appId}/users/${userId}/app_users`;
  const privateDocsCollectionPath = `artifacts/${appId}/users/${userId}/app_documents`;

  useEffect(() => {
    if (!db || !isAuthReady || !userId) return;

    // Listen for users (needed for dropdown names)
    const usersQuery = collection(db, privateUsersCollectionPath);
    const unsubscribeUsers = onSnapshot(usersQuery, (snapshot) => {
      const usersData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setUsers(usersData);
    }, (error) => console.error("Error fetching users:", error));

    // Listen for documents (needed to get content and assignments)
    const docsQuery = collection(db, privateDocsCollectionPath);
    const unsubscribeDocs = onSnapshot(docsQuery, (snapshot) => {
      const docsData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setDocuments(docsData);
    }, (error) => console.error("Error fetching documents:", error));

    return () => {
      unsubscribeUsers();
      unsubscribeDocs();
    };
  }, [db, userId, isAuthReady, privateUsersCollectionPath, privateDocsCollectionPath]);

  // Helper function to display temporary messages
  const showMessage = (msg, type) => {
    setMessage(msg);
    setMessageType(type);
    setTimeout(() => setMessage(''), 3000); // Clear message after 3 seconds
  };

  // Handles user selection from the dropdown and displays their assigned content
  const handleUserSelect = (e) => {
    const selectedUserId = e.target.value;
    setSelectedUser(selectedUserId);
    setDisplayedContent([]); // Clear previous content
    setGeneratedUserContentSummary(''); // Clear previous summary

    if (!selectedUserId) {
      showMessage('', 'info'); // Clear message if no user is selected
      return;
    }

    const contentToShow = [];
    const userDisplayName = users.find(u => u.id === selectedUserId)?.name || "Selected User";

    if (documents.length === 0) {
      showMessage(`No documents available to view. Please contact your administrator.`, 'info');
      return;
    }

    documents.forEach(docItem => {
      const assignedPagesString = docItem.assignedTo && docItem.assignedTo[selectedUserId];
      if (assignedPagesString) {
        const pageNumbers = parsePageRanges(assignedPagesString);
        const docContentArray = docItem.content || [];
        const accessiblePages = [];

        pageNumbers.forEach(pageIdx => {
          // Adjust for 0-based indexing for array, but show 1-based to user
          if (pageIdx > 0 && pageIdx <= docContentArray.length) {
            accessiblePages.push(`Page ${pageIdx}: ${docContentArray[pageIdx - 1]}`);
          }
        });

        if (accessiblePages.length > 0) {
          contentToShow.push({
            docId: docItem.id, // Add docId for unique key if needed
            docName: docItem.name,
            docType: docItem.type,
            pages: accessiblePages,
          });
        }
      }
    });

    if (contentToShow.length === 0) {
      showMessage(`No content assigned to ${userDisplayName}.`, 'info');
    } else {
      setMessage(''); // Clear any previous messages if content is found
    }
    setDisplayedContent(contentToShow);
  };

  // Helper to parse page ranges (e.g., "1-5, 8") into an array of page numbers
  const parsePageRanges = (rangeString) => {
    const pages = new Set();
    rangeString.split(',').forEach(part => {
      const trimmedPart = part.trim();
      if (trimmedPart.includes('-')) {
        const [start, end] = trimmedPart.split('-').map(Number);
        for (let i = start; i <= end; i++) {
          pages.add(i);
        }
      } else if (trimmedPart) {
        pages.add(Number(trimmedPart));
      }
    });
    return Array.from(pages).sort((a, b) => a - b);
  };

  // Gemini API Integration: Summarize User's Assigned Content
  const handleSummarizeUserContent = async () => {
    const allUserContent = displayedContent.flatMap(doc => doc.pages).join('\n');

    if (!allUserContent.trim()) {
      showMessage("No content to summarize for the selected user.", "info");
      return;
    }

    setIsSummarizingUserContent(true);
    setGeneratedUserContentSummary(''); // Clear previous summary
    showMessage("Generating summary...", "info");

    const prompt = `Summarize the following product catalog content that has been assigned to a user. Each line is a page/section from different documents. Combine and summarize all of it concisely:\n\n${allUserContent}`;

    try {
      let chatHistory = [];
      chatHistory.push({ role: "user", parts: [{ text: prompt }] });
      const payload = { contents: chatHistory };
      const apiKey = ""; // Canvas will provide this at runtime
      const apiUrl = `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.0-flash:generateContent?key=${apiKey}`;

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      const result = await response.json();

      if (result.candidates && result.candidates.length > 0 &&
          result.candidates[0].content && result.candidates[0].content.parts &&
          result.candidates[0].content.parts.length > 0) {
        const text = result.candidates[0].content.parts[0].text;
        setGeneratedUserContentSummary(text);
        showMessage("Content summary generated successfully!", "success");
      } else {
        showMessage("Failed to generate summary. No content found in response.", "error");
        console.error("Gemini API response structure unexpected:", result);
      }
    } catch (e) {
      console.error("Error calling Gemini API for user content summary:", e);
      showMessage("Error generating summary. Please try again later.", "error");
    } finally {
      setIsSummarizingUserContent(false);
    }
  };

  return (
    <div className="p-4 sm:p-8 bg-gradient-to-br from-[#F4F4F4] to-[#E0E0E0] min-h-screen font-sans text-[#333333]">
      <h1 className="text-3xl sm:text-4xl font-extrabold text-[#264653] mb-8 text-center">
        User Viewer
      </h1>

      <Message message={message} type={messageType} />

      <section className="mb-10 bg-white p-6 sm:p-8 rounded-2xl shadow-xl border border-gray-200 transition-all duration-300 hover:shadow-2xl animate-fade-in-up">
        <h2 className="text-2xl font-bold text-[#264653] mb-6 flex items-center">
          <Search className="w-7 h-7 mr-3 text-[#2A9D8F]" />
          Select Your Name
        </h2>
        <select
          value={selectedUser}
          onChange={handleUserSelect}
          className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2A9D8F] focus:border-transparent outline-none transition duration-200 bg-white text-base text-[#333333]"
        >
          <option value="">Select your name from the list</option>
          {users.map((user) => (
            <option key={user.id} value={user.id}>{user.name}</option>
          ))}
        </select>
        {users.length === 0 && (
            <p className="text-[#666666] italic mt-3 text-sm">No users found. Please ask an administrator to add users.</p>
        )}
      </section>

      {selectedUser && (
        <section className="mb-10 bg-white p-6 sm:p-8 rounded-2xl shadow-xl border border-gray-200 transition-all duration-300 hover:shadow-2xl animate-fade-in">
          <h2 className="text-2xl font-bold text-[#264653] mb-6 flex items-center">
            <FileText className="w-7 h-7 mr-3 text-[#2A9D8F]" />
            Your Assigned Documents
          </h2>
          {displayedContent.length === 0 ? (
            <p className="text-[#666666] italic">No content assigned to you yet for the selected user.</p>
          ) : (
            <>
              {/* Button to summarize all assigned content */}
              <button
                onClick={handleSummarizeUserContent}
                className="w-full px-4 py-2 mb-4 bg-gradient-to-r from-[#E9C46A] to-[#F4A261] text-[#264653] font-semibold rounded-lg shadow-md hover:from-[#F4A261] hover:to-[#E9C46A] focus:outline-none focus:ring-2 focus:ring-[#E9C46A] focus:ring-offset-2 transition duration-200 transform hover:scale-105 active:scale-95 text-base flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={isSummarizingUserContent || displayedContent.length === 0}
              >
                <Sparkles className="w-5 h-5 mr-2" />
                {isSummarizingUserContent ? (
                  <>
                    <div className="w-4 h-4 border-2 border-t-2 border-[#264653] border-solid rounded-full animate-spin mr-2"></div>
                    Summarizing...
                  </>
                ) : (
                  'Summarize All Assigned Content'
                )}
              </button>

              {/* Display generated user content summary */}
              {generatedUserContentSummary && !isSummarizingUserContent && (
                <div className="mt-3 mb-6 p-4 bg-[#264653]/10 border border-[#264653]/20 rounded-md text-sm text-[#264653] animate-fade-in shadow-inner">
                  <p className="font-semibold mb-1">Your Content Summary:</p>
                  <p>{generatedUserContentSummary}</p>
                </div>
              )}

              {/* Display individual assigned content */}
              <div className="space-y-6">
                {displayedContent.map((docItem) => (
                  <div key={docItem.docId} className="bg-[#F4F4F4] p-4 rounded-lg border border-gray-200 shadow-sm animate-fade-in-up">
                    <h3 className="text-xl font-semibold text-[#333333] mb-3 border-b border-gray-200 pb-2">
                      {docItem.docName} <span className="text-sm font-normal text-[#666666]">({docItem.type.toUpperCase()})</span>
                    </h3>
                    <div className="space-y-2 text-[#333333]">
                      {docItem.pages.map((page, pageIdx) => (
                        <p key={pageIdx} className="p-2 bg-white rounded-md text-base border border-gray-200 shadow-inner">
                          {page}
                        </p>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </section>
      )}
    </div>
  );
};

const App = () => {
  const [isAdminView, setIsAdminView] = useState(true); // Default to admin view

  return (
    <AppProvider>
      <div className="min-h-screen bg-[#F4F4F4]">
        {/* Tailwind CSS CDN */}
        <script src="https://cdn.tailwindcss.com"></script>
        {/* Inter font for better aesthetics */}
        <link href="https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap" rel="stylesheet" />
        <style>
          {`
            body {
              font-family: 'Inter', sans-serif;
            }
            /* Custom scrollbar for textareas */
            textarea::-webkit-scrollbar {
              width: 8px;
            }
            textarea::-webkit-scrollbar-track {
              background: #F4F4F4;
              border-radius: 10px;
            }
            textarea::-webkit-scrollbar-thumb {
              background: #2A9D8F; /* Primary Color */
              border-radius: 10px;
            }
            textarea::-webkit-scrollbar-thumb:hover {
              background: #264653; /* Secondary Color */
            }

            /* Animations for a professional feel */
            .animate-fade-in {
              animation: fadeIn 0.5s ease-out forwards;
            }
            .animate-fade-in-up {
                animation: fadeInUp 0.6s ease-out forwards;
            }
            @keyframes fadeIn {
              from { opacity: 0; }
              to { opacity: 1; }
            }
            @keyframes fadeInUp {
                from { opacity: 0; transform: translateY(20px); }
                to { opacity: 1; transform: translateY(0); }
            }
            @keyframes spin {
              from { transform: rotate(0deg); }
              to { transform: rotate(360deg); }
            }
            .animate-spin {
                animation: spin 1s linear infinite;
            }
          `}
        </style>
        <header className="bg-gradient-to-r from-[#264653] to-[#2A9D8F] text-white p-4 shadow-xl">
          <div className="max-w-7xl mx-auto flex flex-col sm:flex-row justify-between items-center">
            <div className="text-2xl sm:text-3xl font-bold mb-4 sm:mb-0">
              FocusDocs
            </div>
            <nav className="flex space-x-2 sm:space-x-4">
              <button
                onClick={() => setIsAdminView(true)}
                className={`py-2 px-4 sm:px-6 rounded-lg font-semibold transition duration-300 ease-in-out transform hover:scale-105 active:scale-95 text-sm sm:text-base flex items-center space-x-2 ${
                  isAdminView ? 'bg-white text-[#2A9D8F] shadow-md ring-2 ring-[#2A9D8F]/50' : 'bg-transparent text-white hover:bg-white/20'
                }`}
              >
                <Settings className="w-5 h-5" />
                <span>Admin Dashboard</span>
              </button>
              <button
                onClick={() => setIsAdminView(false)}
                className={`py-2 px-4 sm:px-6 rounded-lg font-semibold transition duration-300 ease-in-out transform hover:scale-105 active:scale-95 text-sm sm:text-base flex items-center space-x-2 ${
                  !isAdminView ? 'bg-white text-[#2A9D8F] shadow-md ring-2 ring-[#2A9D8F]/50' : 'bg-transparent text-white hover:bg-white/20'
                }`}
              >
                <Eye className="w-5 h-5" />
                <span>User Viewer</span>
              </button>
            </nav>
          </div>
        </header>

        <main className="container mx-auto my-8 px-4 sm:px-0">
          {isAdminView ? <Dashboard /> : <UserView />}
        </main>

        {/* Footer */}
        <footer className="bg-[#264653] text-white text-center py-4 text-sm mt-12">
          <div className="max-w-7xl mx-auto px-4">
            &copy; {new Date().getFullYear()} FocusDocs. Made with ❤️ by Abhishek Arun.
          </div>
        </footer>

      </div>
    </AppProvider>
  );
};

export default App;
